// BBQ filter-ux phase 1b (PLAN-FILTER-UX.md "Phase 1b"). Four small controllers for the grill-page filters.
// Lifecycle (§7): Horizon's morph keeps matching custom elements and calls updatedCallback(), not
// connectedCallback(), so every controller does its work in an idempotent reconcile() called from both, reads all
// state from the current DOM, and listens on itself (delegation), so morphed children need no rebinding.
import { Component } from '@theme/component';

const money = (n) => `$${Math.round(n).toLocaleString('en-US')}`;

/**
 * Built-In width tiles (§2). Each visible tile checkbox has no name (not a form field); the real width_in inputs
 * for its group are hidden members inside the same form. The tile wrapper routes `change` here through its own
 * on:change, so the enclosing facet-inputs-component never sees it and the form submits exactly once.
 */
class BbqWidthGroups extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.reconcile();
  }

  updatedCallback() {
    super.updatedCallback();
    this.reconcile();
  }

  /**
   * Horizon's declarative dispatch passes a proxied event whose `target` is the element carrying on:change
   * (the tile wrapper), not the checkbox, so find the tile inside it.
   * @param {Event} event
   */
  toggle(event) {
    const t = /** @type {Element|null} */ (event.target);
    const tile = t instanceof HTMLInputElement ? t : t?.querySelector('input[data-bbq-group]');
    if (!(tile instanceof HTMLInputElement)) return;
    const group = tile.dataset.bbqGroup;
    for (const m of this.querySelectorAll(`input[data-bbq-member="${group}"]`)) {
      /** @type {HTMLInputElement} */ (m).checked = tile.checked;
    }
    const form = this.closest('facets-form-component');
    // @ts-ignore - FacetsFormComponent
    if (form && typeof form.updateFilters === 'function') form.updateFilters();
  }

  reconcile() {
    for (const tile of this.querySelectorAll('input[data-bbq-group]')) {
      const t = /** @type {HTMLInputElement} */ (tile);
      const members = [...this.querySelectorAll(`input[data-bbq-member="${t.dataset.bbqGroup}"]`)];
      const on = members.filter((m) => /** @type {HTMLInputElement} */ (m).checked).length;
      t.checked = members.length > 0 && on === members.length;
      t.indeterminate = on > 0 && on < members.length;
      t.closest('.bbq-facet-option')?.classList.toggle('bbq-facet-option--mixed', t.indeterminate);
      // filter-fire F1 §6: Item type tiles announce the partial state in text, not only by the dashed border.
      const note = /** @type {HTMLElement|null} */ (t.closest('.bbq-facet-option')?.querySelector('.bbq-mixed-note') ?? null);
      if (note) note.hidden = !t.indeterminate;
    }
  }
}

/**
 * filter-fire F1 §6a: one chip per Item type group. The chip is Horizon's own facet-remove-component (its
 * removeFilter reads dataset.url at click time). Its data-url is the first active member's url_to_remove; this
 * wrapper strips the other active members, listed in hidden [data-bbq-strip-value] children, with URLSearchParams.
 * Idempotent: a URL already stripped is unchanged.
 */
export function stripGroupValues(rawUrl, param, values, base = 'https://x.invalid/') {
  const url = new URL(rawUrl, base);
  const drop = new Set(values);
  const kept = [...url.searchParams.entries()].filter(([k, v]) => !(k === param && drop.has(v)));
  url.search = new URLSearchParams(kept).toString();
  return url.pathname + url.search;
}

class BbqGroupChip extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.reconcile();
  }

  updatedCallback() {
    super.updatedCallback();
    this.reconcile();
  }

  reconcile() {
    const chip = /** @type {HTMLElement|null} */ (this.querySelector('facet-remove-component'));
    const param = this.dataset.bbqParam;
    if (!chip || !param || !chip.dataset.url) return;
    const values = [...this.querySelectorAll('[data-bbq-strip-value]')].map((s) => /** @type {HTMLElement} */ (s).dataset.bbqStripValue || '');
    if (!values.length) return;
    chip.dataset.url = stripGroupValues(chip.dataset.url, param, values, location.href);
  }
}

/**
 * filter-fire F1 (verification 6): keeps keyboard focus inside the facets form across the AJAX re-render. A
 * document-level focusin listener (attached once, so it survives every morph) records a stable key for the focused
 * control; each fire facets form carries an empty <bbq-focus-keeper>, which watches its form for re-render
 * mutations and, if focus has fallen to <body> (or out of the form), puts it back on the matching control, else on
 * that facet's summary. It never moves focus that is already valid. Grill pages render no keeper.
 */
let bbqFocusKey = null;
document.addEventListener('focusin', (event) => {
  const el = /** @type {HTMLElement} */ (event.target);
  const formEl = el?.closest?.('facets-form-component');
  const keeper = formEl?.querySelector('bbq-focus-keeper');
  if (!formEl || !keeper || !(el instanceof HTMLElement)) return;
  const input = el instanceof HTMLInputElement ? el : null;
  bbqFocusKey = {
    form: keeper.getAttribute('data-bbq-form'),
    group: input?.dataset.bbqGroup ?? null,
    thumb: input?.dataset.bbqThumb ?? null,
    name: input?.name || null,
    value: input?.value ?? null,
    id: el.id || null,
    facet: el.closest('details[id^="Facet-Details-"]')?.id ?? null,
  };
}, true);

class BbqFocusKeeper extends HTMLElement {
  #observer = null;
  #pending = false;

  connectedCallback() {
    const formEl = this.closest('facets-form-component');
    if (!formEl) return;
    this.#observer = new MutationObserver(() => this.#schedule());
    this.#observer.observe(formEl, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #schedule() {
    if (this.#pending) return;
    this.#pending = true;
    requestAnimationFrame(() => requestAnimationFrame(() => { this.#pending = false; this.restore(); }));
  }

  restore() {
    const formEl = this.closest('facets-form-component');
    const key = bbqFocusKey;
    if (!formEl || !key || key.form !== this.getAttribute('data-bbq-form')) return;
    // Only reclaim focus that the re-render dropped (to <body> or nothing); focus the user moved stays put.
    const active = document.activeElement;
    if (active && active !== document.body) return;
    if (formEl.getClientRects().length === 0) return; // this form is not on screen (rail on phones, closed drawer)
    const q = (sel) => /** @type {HTMLElement|null} */ (formEl.querySelector(sel));
    const esc = (v) => (window.CSS?.escape ? CSS.escape(v) : v.replace(/["\\]/g, '\\$&'));
    let target = null;
    if (key.group != null) target = q(`input[data-bbq-group="${esc(key.group)}"]`);
    if (!target && key.thumb) target = q(`input[data-bbq-thumb="${esc(key.thumb)}"]`);
    if (!target && key.name && key.value != null) target = q(`input[name="${esc(key.name)}"][value="${esc(key.value)}"]:not([hidden])`);
    if (!target && key.id) target = /** @type {HTMLElement|null} */ (document.getElementById(key.id));
    if (target && !formEl.contains(target)) target = null;
    if (!target && key.facet) target = q(`#${esc(key.facet)} > summary`);
    if (target) target.focus({ preventScroll: true });
  }
}

/**
 * Desktop brand search (§4). Not a form field. Searches every rendered brand row; while a query is active it lifts
 * Horizon's show-more hiding and hides the Show all button, and restores both exactly when cleared.
 */
class BbqBrandSearch extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('input', this.#onInput);
    this.reconcile();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('input', this.#onInput);
  }

  updatedCallback() {
    super.updatedCallback();
    this.reconcile();
  }

  #onInput = () => this.reconcile();

  /**
   * The search box sits inside the brand facet's facet-inputs-component, whose on:change submits the form. A text
   * input fires change on blur, so this element carries its own on:change (nearest wins) and swallows it here.
   */
  ignore() {}

  get #panel() {
    return this.closest('.facets__panel') || this.parentElement;
  }

  reconcile() {
    const input = /** @type {HTMLInputElement|null} */ (this.querySelector('input[type="search"]'));
    const panel = this.#panel;
    if (!input || !panel) return;
    const q = input.value.trim().toLowerCase();
    const rows = /** @type {HTMLElement[]} */ ([...panel.querySelectorAll('.facets__inputs-list-item')]);
    const showMore = /** @type {HTMLElement|null} */ (panel.querySelector('.show-more__button'));
    for (const row of rows) {
      if (q) {
        if (row.classList.contains('hidden')) {
          row.classList.remove('hidden');
          row.dataset.bbqUnhid = '1';
        }
        const label = (row.querySelector('input')?.value || row.textContent || '').toLowerCase();
        row.style.display = label.includes(q) ? '' : 'none';
      } else {
        row.style.display = '';
        if (row.dataset.bbqUnhid) {
          row.classList.add('hidden');
          delete row.dataset.bbqUnhid;
        }
      }
    }
    if (showMore) showMore.style.display = q ? 'none' : '';
  }
}

/**
 * Price slider (§5). Two labelled range controls in whole dollars over Horizon's hidden backing fields. `input`
 * only syncs locally; the native `change` bubbles to price-facet-component's on:change (updatePriceFilterAndResults),
 * which is the single submit path. At an endpoint the backing field is emptied so no price param remains.
 */
class BbqPriceSlider extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('input', this.#onInput);
    this.addEventListener('keydown', this.#onKeyDown);
    this.reconcile();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * price-facet-component (an ancestor) cancels every key except digits and arrows, written for its text boxes;
   * that would also kill Home / End / PageUp / PageDown on the range thumbs, so keep their keydown from reaching it.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.type === 'range') event.stopPropagation();
  };

  updatedCallback() {
    super.updatedCallback();
    this.reconcile();
  }

  get #parts() {
    const facet = this.closest('price-facet-component');
    return {
      lo: /** @type {HTMLInputElement|null} */ (this.querySelector('input[data-bbq-thumb="min"]')),
      hi: /** @type {HTMLInputElement|null} */ (this.querySelector('input[data-bbq-thumb="max"]')),
      gte: /** @type {HTMLInputElement|null} */ (facet?.querySelector('input[name$="price.gte"]') ?? null),
      lte: /** @type {HTMLInputElement|null} */ (facet?.querySelector('input[name$="price.lte"]') ?? null),
      range: /** @type {HTMLElement|null} */ (this.querySelector('.bbq-price__range')),
      fill: /** @type {HTMLElement|null} */ (this.querySelector('.bbq-price__fill')),
    };
  }

  /** @param {Event} event */
  #onInput = (event) => {
    const { lo, hi, gte, lte } = this.#parts;
    if (!lo || !hi || !gte || !lte) return;
    const max = Number(hi.max);
    let a = Number(lo.value);
    let b = Number(hi.value);
    if (event.target === lo && a > b - 1) a = Math.max(0, b - 1);
    if (event.target === hi && b < a + 1) b = Math.min(max, a + 1);
    lo.value = String(a);
    hi.value = String(b);
    gte.value = a <= 0 ? '' : String(a);
    lte.value = b >= max ? '' : String(b);
    this.#paint(a, b, max);
  };

  /** @param {number} a @param {number} b @param {number} max */
  #paint(a, b, max) {
    const { lo, hi, range, fill } = this.#parts;
    if (lo) lo.setAttribute('aria-valuetext', money(a));
    if (hi) hi.setAttribute('aria-valuetext', money(b));
    if (range) range.textContent = `${money(a)} – ${money(b)}`;
    if (fill && max > 0) {
      fill.style.left = `${(a / max) * 100}%`;
      fill.style.right = `${100 - (b / max) * 100}%`;
    }
  }

  reconcile() {
    const { lo, hi } = this.#parts;
    if (!lo || !hi) return;
    this.#paint(Number(lo.value), Number(hi.value), Number(hi.max));
  }
}

/** Collection price histogram (§5): decorative, unfiltered collection spread, cached per handle. */
const histogramCache = new Map();
class BbqPriceHistogram extends Component {
  #failed = false;
  #observer = null;

  connectedCallback() {
    super.connectedCallback();
    // filter-fire F1: Horizon's morph empties this element on every AJAX filter render (the server markup has no
    // bars) without calling updatedCallback here, so the bars vanished after the first filter (live since 1b).
    // Watch our own children and redraw when a re-render empties them; a failed fetch never retries in a loop.
    this.#observer = new MutationObserver(() => { if (!this.#failed && this.childElementCount === 0) this.reconcile(); });
    this.#observer.observe(this, { childList: true });
    this.reconcile();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  updatedCallback() {
    super.updatedCallback();
    this.reconcile();
  }

  // filter-fire F1 §8: the cache holds the in-flight Promise, so the rail and drawer histograms share one fetch.
  // A rejected fetch is dropped from the cache so a later render can retry.
  #prices(handle) {
    if (histogramCache.has(handle)) return histogramCache.get(handle);
    const p = this.#load(handle);
    histogramCache.set(handle, p);
    p.catch(() => { if (histogramCache.get(handle) === p) histogramCache.delete(handle); });
    return p;
  }

  async #load(handle) {
    const key = `bbq-hist:${handle}`;
    try {
      const cached = sessionStorage.getItem(key);
      if (cached) return JSON.parse(cached);
    } catch {}
    const prices = [];
    for (let page = 1; page <= 4; page++) {
      const res = await fetch(`/collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`);
      if (!res.ok) throw new Error(`products.json ${res.status}`);
      const { products } = await res.json();
      for (const p of products) {
        const v = p.variants?.map((x) => Number(x.price)).filter((n) => n > 0);
        if (v?.length) prices.push(Math.min(...v));
      }
      if (products.length < 250) break;
    }
    try { sessionStorage.setItem(key, JSON.stringify(prices)); } catch {}
    return prices;
  }

  reconcile() {
    const handle = this.dataset.handle;
    const max = Number(this.dataset.max);
    if (!handle || !max) return;
    const draw = async () => {
      try {
        const prices = await this.#prices(handle);
        const bins = new Array(24).fill(0);
        for (const p of prices) bins[Math.min(23, Math.floor((p / max) * 24))]++;
        const top = Math.max(1, ...bins);
        this.replaceChildren(...bins.map((n) => {
          const bar = document.createElement('span');
          bar.className = 'bbq-price__bar';
          bar.style.height = `${n ? Math.max(8, (n / top) * 100) : 0}%`;
          return bar;
        }));
      } catch {
        this.#failed = true;
        this.replaceChildren();
      }
    };
    if ('requestIdleCallback' in window) window.requestIdleCallback(() => draw(), { timeout: 2000 });
    else setTimeout(draw, 200);
  }
}

for (const [name, cls] of [
  ['bbq-width-groups', BbqWidthGroups],
  ['bbq-brand-search', BbqBrandSearch],
  ['bbq-price-slider', BbqPriceSlider],
  ['bbq-price-histogram', BbqPriceHistogram],
  ['bbq-group-chip', BbqGroupChip],
  ['bbq-focus-keeper', BbqFocusKeeper],
]) {
  if (!customElements.get(name)) customElements.define(name, cls);
}
